import React, { useMemo, useState } from "react";
import Card from "./Card";
import { healthVisual } from "../utils/healthVisual";

export default function HealthPanel({ metrics, metricsErr, historyCount }) {
  const [open, setOpen] = useState(false);

  const health = metrics?.health;
  const status = health?.status || (metricsErr ? "bad" : "ok");
  const hv = useMemo(() => healthVisual(status), [status]);

  const reasons = useMemo(() => {
    const r = Array.isArray(health?.reasons) ? health.reasons : [];
    return r.length ? r : ["Alle Werte im normalen Bereich"];
  }, [health]);

  const summary = reasons[0] || "Alle Werte im normalen Bereich";
  const moreCount = Math.max(0, reasons.length - 1);

  const headline =
    status === "ok" ? "Alles in Ordnung" : status === "warn" ? "Eingeschränkt" : "Störung";

  const divider = "1px solid rgba(15,23,42,0.088)";
  const softBg = "rgba(15,23,42,0.044)";
  const darkBg = "#f8f9fb";

  return (
    <Card
      title="Systemzustand"
      right={
        <button
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          style={{
            height: 34,
            padding: "0 12px",
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: open ? "var(--brand-soft)" : "#ffffff",
            color: open ? "var(--brand)" : "var(--text)",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 12,
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {open ? "Ausblenden" : "Details"}
          <span
            aria-hidden
            style={{
              display: "inline-block",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 140ms ease",
              opacity: 0.9,
            }}
          >
            ▾
          </span>
        </button>
      }
    >
      {/* heartbeat animation */}
      <style>{`
        @keyframes hpBeat {
          0%   { transform: scale(1);   opacity: .95; filter: saturate(1); }
          20%  { transform: scale(1.25); opacity: 1;  filter: saturate(1.2); }
          40%  { transform: scale(1);   opacity: .95; filter: saturate(1); }
          55%  { transform: scale(1.15); opacity: 1;  filter: saturate(1.15); }
          70%  { transform: scale(1);   opacity: .95; filter: saturate(1); }
          100% { transform: scale(1);   opacity: .95; filter: saturate(1); }
        }
        @keyframes hpPulseRing {
          0%   { transform: scale(1);   opacity: .55; }
          30%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>

      {/* STATUS BANNER */}
      <div
        style={{
          position: "relative",
          borderRadius: 14,
          padding: 12,
          background: darkBg,
          border: divider,
          overflow: "hidden",
        }}
      >
        {/* subtle glow strip */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, ${hv.bg} 0%, rgba(0,0,0,0) 58%)`,
            opacity: 0.85,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* Left: heartbeat dot + status (NO border around dot container) */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 260 }}>
            {/* container without border */}
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 12,
                background: "transparent", // no box look
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                position: "relative",
              }}
              title={headline}
            >
              {/* pulse ring */}
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: hv.fg,
                  opacity: 0.35,
                  animation: "hpPulseRing 1.6s ease-out infinite",
                }}
              />
              {/* heartbeat dot */}
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: hv.fg,
                  boxShadow: `0 0 0 6px ${hv.bg}`,
                  animation: "hpBeat 1.25s ease-in-out infinite",
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 900,
                    letterSpacing: 0.2,
                    color: hv.fg,
                  }}
                >
                  {headline}
                </span>

                <span style={{ fontSize: 12, opacity: 0.78 }}>
                  {moreCount > 0 ? `${moreCount + 1} Hinweise` : "Alle Werte"}
                </span>
              </div>

              <div
                style={{
                  fontSize: 12,
                  opacity: 0.9,
                  lineHeight: 1.35,
                  maxWidth: 560,
                }}
                title={reasons.join(" • ")}
              >
                {summary}
                {moreCount > 0 ? (
                  <span style={{ opacity: 0.75 }}>{"  "}+{moreCount} weitere</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Right: history chip */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 12,
              background: softBg,
              border: divider,
              whiteSpace: "nowrap",
              fontSize: 12,
              opacity: 0.9,
            }}
          >
            <span style={{ opacity: 0.75 }}>Verlauf</span>
            <span style={{ fontWeight: 900 }}>{historyCount}</span>
            <span style={{ opacity: 0.75 }}>Messpunkte</span>
          </div>
        </div>

        {/* Divider only when open (prevents that “green fade border” above the hint) */}
        {open && <div style={{ marginTop: 12, borderTop: divider }} />}

        {/* DETAILS */}
        {open ? (
          <div style={{ position: "relative", marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.9 }}>
                Hinweise
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>{reasons.length} Einträge</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              {reasons.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "#f8f9fb",
                    border: "1px solid rgba(15,23,42,0.066)",
                    fontSize: 12,
                    lineHeight: 1.4,
                    opacity: 0.95,
                  }}
                >
                  <span
                    style={{
                      marginTop: 5,
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: i === 0 ? hv.fg : "#6b7280",
                      boxShadow: i === 0 ? `0 0 0 4px ${hv.bg}` : "none",
                      flex: "0 0 auto",
                    }}
                  />
                  <div style={{ flex: 1 }}>{r}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
